import { useState, type CSSProperties } from "react";
import { useWrappedApiCalls, type WrappedApiCall } from "../../hooks/use-wrapped-apis";

/**
 * Sprint 16b — call log for a wrapped API. Shows the last 100 calls from the
 * audit log, filterable by status, polling every 5s while open. Each row expands
 * to show the full metadata (retry count, idempotency key, breaker state, error).
 */
const FILTERS = [
  { key: "", label: "All" },
  { key: "success", label: "Success" },
  { key: "error", label: "Error" },
  { key: "circuit_open", label: "Circuit open" },
] as const;

export function CallLogTab({ slug, active }: { slug: string; active: boolean }) {
  const [filter, setFilter] = useState<string>("");
  const [expanded, setExpanded] = useState<string | null>(null);
  const calls = useWrappedApiCalls(slug, { statusFilter: filter || undefined, enabled: active });

  const rows = calls.data ?? [];

  return (
    <div>
      <div style={{ display: "flex", gap: 6, marginBottom: 12, alignItems: "center", flexWrap: "wrap" }}>
        {FILTERS.map((f) => (
          <button key={f.key} type="button" onClick={() => setFilter(f.key)} style={chip(filter === f.key)}>
            {f.label}
          </button>
        ))}
        <span style={{ fontSize: 11, color: "var(--color-text-tertiary)", marginLeft: "auto" }}>
          {calls.isFetching ? "refreshing…" : `${rows.length} call${rows.length === 1 ? "" : "s"} · live`}
        </span>
      </div>

      {rows.length === 0 ? (
        <div style={{ fontSize: 13, color: "var(--color-text-tertiary)", padding: "16px 0", textAlign: "center" }}>
          {calls.isLoading ? "Loading…" : "No calls yet."}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column" }}>
          <div style={{ ...rowStyle, borderBottom: "0.5px solid var(--color-border-secondary)" }}>
            <span style={{ ...colTime, ...headCell }}>Time</span>
            <span style={{ ...colOp, ...headCell }}>Operation</span>
            <span style={{ ...colStatus, ...headCell }}>Status</span>
            <span style={{ ...colLatency, ...headCell }}>Latency</span>
            <span style={{ ...colActor, ...headCell }}>Actor</span>
          </div>
          {rows.map((c) => (
            <div key={c.id}>
              <button type="button" onClick={() => setExpanded(expanded === c.id ? null : c.id)} style={{ ...rowStyle, ...rowButton }}>
                <span style={colTime} title={c.at}>{formatTime(c.at)}</span>
                <code style={colOp} title={c.tool_name ?? c.operation_id ?? ""}>{c.operation_id ?? c.tool_name ?? "—"}</code>
                <span style={colStatus}><span style={statusBadge(c.status)}>{statusLabel(c)}</span></span>
                <span style={colLatency}>{c.latency_ms == null ? "—" : `${c.latency_ms}ms`}</span>
                <span style={colActor} title={c.actor}>{c.actor_host_kind ?? shortActor(c.actor)}</span>
              </button>
              {expanded === c.id && <CallDetail call={c} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CallDetail({ call }: { call: WrappedApiCall }) {
  const fields: Array<[string, string]> = [
    ["Method", call.method ?? "—"],
    ["HTTP status", call.http_status == null ? "—" : String(call.http_status)],
    ["Retries", String(call.retry_count)],
    ["Breaker", call.circuit_breaker_state ?? "—"],
    ["Idempotency key", call.idempotency_key ?? "—"],
    ["Actor", call.actor],
    ["Host", call.actor_host_kind ?? "—"],
    ["Tool", call.tool_name ?? "—"],
  ];
  return (
    <div style={detailBox}>
      {fields.map(([k, v]) => (
        <div key={k} style={{ display: "flex", gap: 8, padding: "2px 0", fontSize: 11 }}>
          <span style={{ width: 110, flexShrink: 0, color: "var(--color-text-tertiary)" }}>{k}</span>
          <span style={{ color: "var(--color-text-secondary)", fontFamily: "var(--font-mono)", wordBreak: "break-all", minWidth: 0 }}>{v}</span>
        </div>
      ))}
    </div>
  );
}

function statusLabel(c: WrappedApiCall): string {
  if (c.status === "success") return c.http_status ? String(c.http_status) : "ok";
  if (c.circuit_breaker_state === "open" && c.http_status == null) return "circuit";
  return c.http_status ? String(c.http_status) : "error";
}

function shortActor(actor: string): string {
  return actor.length > 10 ? `${actor.slice(0, 8)}…` : actor;
}

function formatTime(iso: string): string {
  // The audit timestamp is UTC ("YYYY-MM-DD HH:MM:SS"); show HH:MM:SS.
  const m = /(\d{2}:\d{2}:\d{2})/.exec(iso);
  return m?.[1] ?? iso;
}

function statusBadge(status: string): CSSProperties {
  const ok = status === "success";
  return {
    fontSize: 10,
    fontWeight: 600,
    fontFamily: "var(--font-mono)",
    color: ok ? "var(--c-green-800, var(--color-text-secondary))" : "var(--c-red-600, var(--color-text-primary))",
    background: ok ? "var(--c-green-50, var(--color-background-secondary))" : "var(--c-red-50, var(--color-background-secondary))",
    borderRadius: "var(--border-radius-sm)",
    padding: "1px 6px",
  };
}

function chip(on: boolean): CSSProperties {
  return {
    fontSize: 12,
    padding: "4px 10px",
    borderRadius: "var(--border-radius-sm)",
    border: on ? "1px solid var(--color-text-info)" : "var(--kuvio-border-default)",
    background: on ? "var(--c-blue-50, var(--color-background-secondary))" : "var(--color-background-primary)",
    color: on ? "var(--color-text-info)" : "var(--color-text-secondary)",
    cursor: "pointer",
    fontFamily: "var(--font-sans)",
  };
}

const rowStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "7px 0",
  width: "100%",
};

const rowButton: CSSProperties = {
  border: "none",
  background: "transparent",
  borderBottom: "0.5px solid var(--color-border-secondary)",
  cursor: "pointer",
  textAlign: "left",
  fontFamily: "var(--font-sans)",
};

const headCell: CSSProperties = { fontSize: 11, color: "var(--color-text-tertiary)", fontWeight: 500 };
const colTime: CSSProperties = { width: 70, flexShrink: 0, fontSize: 11, color: "var(--color-text-tertiary)", fontFamily: "var(--font-mono)" };
const colOp: CSSProperties = { flex: 1, minWidth: 0, fontSize: 12, color: "var(--color-text-primary)", fontFamily: "var(--font-mono)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
const colStatus: CSSProperties = { width: 60, flexShrink: 0 };
const colLatency: CSSProperties = { width: 64, flexShrink: 0, fontSize: 11, color: "var(--color-text-secondary)" };
const colActor: CSSProperties = { width: 80, flexShrink: 0, fontSize: 11, color: "var(--color-text-tertiary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

const detailBox: CSSProperties = {
  background: "var(--color-background-secondary)",
  borderRadius: "var(--border-radius-sm)",
  padding: "8px 12px",
  margin: "2px 0 6px",
};
